import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, MapPin, Clock, ShieldCheck } from "lucide-react";
import HeroSearch from "@/components/HeroSearch";
import VenueCard from "@/components/VenueCard";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1768851142332-75f3d1b47452?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxiZWF1dGlmdWwlMjBldmVudCUyMHNwYWNlfGVufDB8fHx8MTc4MzUzNDk5Mnww&ixlib=rb-4.1.0&q=85";

const POPULAR = [
    { label: "Private venue hire in London", href: "/venues/united-kingdom/london" },
    { label: "Rooftop venues in Dubai", href: "/hire/rooftop-venues/dubai" },
    { label: "Wedding venues in New York", href: "/events/weddings/new-york" },
    { label: "Christmas parties in Manchester", href: "/events/christmas-parties/manchester" },
    { label: "Private dining rooms in Mayfair", href: "/venues/united-kingdom/london/mayfair" },
    { label: "Corporate events in Toronto", href: "/events/corporate-events/toronto" },
];

const OCCASIONS = [
    { label: "Birthday parties", slug: "birthday-parties", img: "https://images.unsplash.com/photo-1695128861516-d9b48461c04c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxyb29mdG9wJTIwYmFyJTIwcGFydHl8ZW58MHx8fHwxNzgzNTM0OTgyfDA&ixlib=rb-4.1.0&q=85" },
    { label: "Weddings", slug: "weddings", img: "https://images.pexels.com/photos/12954015/pexels-photo-12954015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { label: "Corporate events", slug: "corporate-events", img: "https://images.unsplash.com/photo-1531058020387-3be344556be6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBldmVudCUyMHBlb3BsZXxlbnwwfHx8fDE3ODM1MzQ5ODJ8MA&ixlib=rb-4.1.0&q=85" },
    { label: "Private dining", slug: "private-dining", img: "https://images.unsplash.com/photo-1769638913500-4a0b6ac4561a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwZGluaW5nJTIwcm9vbXxlbnwwfHx8fDE3ODM1MzQ5ODJ8MA&ixlib=rb-4.1.0&q=85" },
];

const VENUE_TYPES_SHOWCASE = [
    { label: "Rooftop venues", slug: "rooftop-venues" },
    { label: "Warehouses", slug: "warehouses" },
    { label: "Private dining rooms", slug: "private-dining-rooms" },
    { label: "Pubs", slug: "pubs" },
    { label: "Restaurants", slug: "restaurants" },
    { label: "Function rooms", slug: "function-rooms" },
    { label: "Galleries", slug: "galleries" },
    { label: "Studios", slug: "studios" },
];

const CITIES = [
    { name: "London", slug: "london", country_slug: "united-kingdom" },
    { name: "New York", slug: "new-york", country_slug: "united-states" },
    { name: "Sydney", slug: "sydney", country_slug: "australia" },
    { name: "Dubai", slug: "dubai", country_slug: "united-arab-emirates" },
    { name: "Manchester", slug: "manchester", country_slug: "united-kingdom" },
    { name: "Toronto", slug: "toronto", country_slug: "canada" },
    { name: "Edinburgh", slug: "edinburgh", country_slug: "united-kingdom" },
    { name: "Birmingham", slug: "birmingham", country_slug: "united-kingdom" },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);

    useSEO({
        title: "Venuely — Private venue hire, worldwide",
        description: "Find and enquire about private venues for hire anywhere in the world. Rooftops, warehouses, private dining rooms and function rooms — all in one place.",
        image: HERO_IMG,
    });

    useEffect(() => {
        api.get("/venues/featured?limit=8").then((r) => setFeatured(r.data.items || [])).catch(() => {});
    }, []);

    return (
        <div>
            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="home-hero">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="Elegant venue" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-background/95" />
                </div>
                <div className="relative container-app pt-20 pb-24 md:pt-28 md:pb-36">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-primary">
                            <Sparkles className="h-3.5 w-3.5" /> Global private venue directory
                        </span>
                        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-semibold text-white drop-shadow-sm">
                            Find beautiful private venues<br />for hire, anywhere.
                        </h1>
                        <p className="mt-4 text-base md:text-lg text-white/90 max-w-xl">
                            Birthdays, dinners, weddings, corporate events — enquire in seconds with venues that fit your vibe and your budget.
                        </p>
                    </div>
                    <div className="mt-8 md:mt-10">
                        <HeroSearch />
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        <span className="text-xs md:text-sm text-white/80 mr-2">Trending:</span>
                        {POPULAR.slice(0, 4).map((p) => (
                            <Link
                                key={p.href}
                                to={p.href}
                                className="text-xs md:text-sm rounded-full bg-white/90 hover:bg-white text-foreground px-3 py-1.5 transition-colors"
                                data-testid={`popular-${p.href.replace(/[^a-z0-9]/gi, "-")}`}
                            >
                                {p.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED VENUES */}
            <section className="container-app py-16 md:py-24">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-secondary">Featured</span>
                        <h2 className="font-display text-3xl md:text-4xl mt-1 font-semibold">Standout private venues</h2>
                    </div>
                    <Link to="/search" className="hidden md:inline-flex items-center gap-1 text-sm font-medium hover:text-primary" data-testid="see-all-venues">
                        See all venues <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                    {featured.map((v, i) => <VenueCard key={v.venue_id} venue={v} index={i} />)}
                    {featured.length === 0 && Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="rounded-xl border border-border bg-muted/30 aspect-[4/3] animate-pulse" />
                    ))}
                </div>
            </section>

            {/* BY OCCASION */}
            <section className="container-app py-8 md:py-12">
                <div className="mb-8">
                    <span className="text-xs font-semibold uppercase tracking-widest text-secondary">Occasions</span>
                    <h2 className="font-display text-3xl md:text-4xl mt-1 font-semibold">Browse by what you're planning</h2>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                    {OCCASIONS.map((o) => (
                        <Link
                            key={o.slug}
                            to={`/events/${o.slug}/london`}
                            className="group relative aspect-[4/5] rounded-xl overflow-hidden hover-card-lift"
                            data-testid={`occasion-${o.slug}`}
                        >
                            <img src={o.img} alt={o.label} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-white">
                                <h3 className="font-display text-xl md:text-2xl font-semibold">{o.label}</h3>
                                <p className="text-xs text-white/80 mt-0.5">See venues →</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CITIES */}
            <section className="container-app py-16 md:py-20">
                <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
                    <div>
                        <span className="text-xs font-semibold uppercase tracking-widest text-secondary">Cities</span>
                        <h2 className="font-display text-3xl md:text-4xl mt-1 font-semibold leading-tight">Cities we cover</h2>
                        <p className="mt-3 text-muted-foreground max-w-md">From London warehouses to Dubai rooftops — discover distinctive spaces in the world's best event cities.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:gap-3">
                        {CITIES.map((c) => (
                            <Link
                                key={c.slug}
                                to={`/venues/${c.country_slug}/${c.slug}`}
                                className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/60 hover:bg-muted/40 transition-colors"
                                data-testid={`city-${c.slug}`}
                            >
                                <MapPin className="h-4 w-4 text-secondary" />
                                <span className="font-medium">{c.name}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* VENUE TYPES */}
            <section className="container-app py-8 md:py-12">
                <span className="text-xs font-semibold uppercase tracking-widest text-secondary">Venue types</span>
                <h2 className="font-display text-3xl md:text-4xl mt-1 font-semibold mb-6">Every kind of private space</h2>
                <div className="flex flex-wrap gap-2 md:gap-3">
                    {VENUE_TYPES_SHOWCASE.map((v) => (
                        <Link key={v.slug} to={`/hire/${v.slug}/london`} className="rounded-full border border-border px-4 py-2 text-sm bg-card hover:bg-muted transition-colors" data-testid={`venue-type-${v.slug}`}>
                            {v.label} in London
                        </Link>
                    ))}
                </div>
            </section>

            {/* OWNER CTA */}
            <section className="container-app py-16 md:py-24">
                <div className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground p-10 md:p-14">
                    <div className="absolute inset-0 subtle-noise opacity-30" />
                    <div className="relative grid md:grid-cols-2 gap-8 items-center">
                        <div>
                            <span className="text-xs font-semibold uppercase tracking-widest text-accent">Venue owners</span>
                            <h2 className="font-display text-3xl md:text-4xl mt-2 font-semibold leading-tight">Get more private hire enquiries.</h2>
                            <p className="mt-3 text-primary-foreground/80 max-w-md">Reach thousands of people planning private events. Listing is free — upgrade for verified and featured placement.</p>
                            <div className="mt-6 flex gap-3">
                                <Button asChild className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="cta-list-your-venue">
                                    <Link to="/list-your-venue">List your venue</Link>
                                </Button>
                                <Button asChild variant="outline" className="rounded-full bg-transparent border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10">
                                    <Link to="/how-it-works">How it works</Link>
                                </Button>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            {[
                                { icon: ShieldCheck, k: "Verified", v: "Build trust with a badge" },
                                { icon: Sparkles, k: "Featured", v: "Top of results" },
                                { icon: Clock, k: "Fast enquiries", v: "Straight to your inbox" },
                            ].map(({ icon: Icon, k, v }) => (
                                <div key={k} className="rounded-xl bg-primary-foreground/10 p-4 border border-primary-foreground/10">
                                    <Icon className="h-5 w-5 text-accent" />
                                    <p className="mt-2 font-semibold">{k}</p>
                                    <p className="text-xs text-primary-foreground/70">{v}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
